import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { LeadApiService } from '../services/lead-api.service';
import { Lead, LeadSearchParams, LeadState, LeadCycle, LeadDisqualificationReason, LeadBulkAction, LeadBulkActionParams, LeadBulkActionResponse, LeadBulkActionFailure } from '../models/lead.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { LeadDetailModalComponent } from '../lead-detail-modal/lead-detail-modal.component';
import { LeadCloseModalComponent, CloseLeadData } from '../lead-close-modal/lead-close-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { CrmUserResponse } from '../../users/models/user.model';
import { Observable, of, map, catchError } from 'rxjs';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-lead-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    LeadDetailModalComponent,
    LeadCloseModalComponent,
    HasPermissionDirective,
    EntitySelectComponent,
    CountrySelectComponent
  ],
  templateUrl: './lead-list.component.html'
})
export class LeadListComponent implements OnInit {
  leads: Lead[] = [];

  /** Código ISO (minúscula) del país derivado del teléfono del contacto, para la bandera. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }
  /** Nombre del país derivado del teléfono (tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país (por prefijo telefónico del contacto). */
  readonly filterCountries = COUNTRIES_DATA;
  filtroPais = '';
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.buscar();
  }
  loading = false;
  error = '';

  // Catalogues for filters
  leadStates: LeadState[] = [];
  leadCycles: LeadCycle[] = [];
  disqualificationReasons: LeadDisqualificationReason[] = [];

  // Filtro de responsable
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_leads';
  filtroOwner: 'all' | 'mine' = localStorage.getItem(this.OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all';

  // Filters
  showFilters = false;
  filtroNombre = '';
  filtroLeadCycle: number | undefined;
  filtroLeadState: number | undefined;
  filtroIsDisqualified: boolean | undefined;

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'lead-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  // Detail modal state
  showDetailModal = false;
  selectedLead: Lead | null = null;

  // Disqualify modal state
  showDisqualifyModal = false;
  leadToDisqualify: Lead | null = null;
  savingDisqualify = false;

  // ── Acciones masivas ──
  selectedLeadIds: number[] = [];
  isBulkActionModalOpen = false;
  bulkAction: LeadBulkAction = 'CHANGE_OWNER';
  isExecutingBulk = false;
  bulkResult: LeadBulkActionResponse | null = null;
  // Responsable elegido para CHANGE_OWNER (vía EntitySelectComponent)
  bulkOwnerId: number | null = null;
  // Etapa elegida para CHANGE_CYCLE (id del catálogo leadCycles)
  bulkCycleId: number | null = null;

  constructor(
    private leadApi: LeadApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarLeads();
  }

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Inc 35: solo catálogos activos en los selectores (filtros + acción masiva
    // "Cambiar etapa"). Los inactivos no deben aparecer como opción.
    this.leadApi.getLeadStates(businessId, true).subscribe({
      next: (data) => this.leadStates = data,
      error: () => { }
    });

    this.leadApi.getLeadCycles(businessId, true).subscribe({
      next: (data) => this.leadCycles = data,
      error: () => { }
    });

    this.leadApi.getDisqualificationReasons(businessId, true).subscribe({
      next: (data) => this.disqualificationReasons = data,
      error: () => { }
    });
  }

  cargarLeads(): void {
    this.loading = true;
    this.error = '';
    // Cualquier recarga (paginación, filtro, búsqueda) invalida la selección previa.
    this.selectedLeadIds = [];

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: LeadSearchParams = {
      businessId,
      searchName: this.filtroNombre || undefined,
      idLeadCycle: this.filtroLeadCycle,
      idLeadState: this.filtroLeadState,
      isDisqualified: this.filtroIsDisqualified,
      phonePrefix: this.filtroPais
        ? COUNTRIES_DATA.find(c => c.code === this.filtroPais)?.dialCode || undefined
        : undefined,
      userPropietyId: this.filtroOwner === 'mine' ? user?.id : undefined,
      page: this.globalPage,
      size: this.pageSize
    };

    this.leadApi.searchLeads(params).subscribe({
      next: (response) => {
        this.leads = response.content;
        this.globalPage = response.number ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los leads';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarLeads();
  }

  setOwnerFilter(value: 'all' | 'mine'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarLeads();
  }

  limpiarFiltro(): void {
    this.filtroOwner = 'all';
    this.filtroNombre = '';
    this.filtroLeadCycle = undefined;
    this.filtroLeadState = undefined;
    this.filtroIsDisqualified = undefined;
    this.filtroPais = '';
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarLeads();
  }

  vistaKanban(): void {
    this.router.navigate(['/leads']);
  }

  nuevoLead(): void {
    this.router.navigate(['/leads/crear']);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  verDetalle(lead: Lead): void {
    this.selectedLead = lead;
    this.showDetailModal = true;
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.selectedLead = null;
  }

  editarLead(lead: Lead): void {
    this.router.navigate(['/leads/editar', lead.id]);
  }

  async eliminarLead(lead: Lead): Promise<void> {
    if (!lead.id) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar lead',
      message: `¿Está seguro de eliminar el lead "${lead.name}"?`
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.leadApi.deleteLead(lead.id, businessId).subscribe({
      next: () => {
        this.cargarLeads();
        this.toast.deleted('Lead');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el lead' });
      }
    });
  }

  abrirDescalificar(lead: Lead): void {
    this.leadToDisqualify = lead;
    this.showDisqualifyModal = true;
  }

  cerrarDescalificar(): void {
    this.showDisqualifyModal = false;
    this.leadToDisqualify = null;
  }

  confirmarDescalificar(data: CloseLeadData): void {
    if (!this.leadToDisqualify?.id) return;

    this.savingDisqualify = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const leadId = this.leadToDisqualify.id;

    // Inc 3/6/38: el BACKEND ya mueve el ciclo a "Reintentando"/"Descalificado"
    // dentro de disqualify. NO se hace change-cycle/update extra en el cliente
    // (esa orquestación duplicada era la causa de tarjetas duplicadas e
    // inconsistencia). Refrescamos con lo que devuelve el backend.
    this.leadApi.disqualifyLead(
      leadId,
      businessId,
      data.disqualificationReasonId,
      data.disqualificationNotes,
      data.retryDate,
      data.isRetryScheduled || undefined
    ).subscribe({
      next: () => {
        this.savingDisqualify = false;
        this.cerrarDescalificar();
        // Inc 3/6: recargar catálogos por si el backend sembró el ciclo
        // "Descalificado" o el tipo "Reintentando" (para los filtros).
        this.cargarCatalogos();
        this.cargarLeads();
        this.toast.success({ title: 'Lead descalificado', message: 'El lead se descalificó correctamente.' });
      },
      error: (err) => {
        this.savingDisqualify = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al descalificar el lead' });
      }
    });
  }

  reabrirLead(lead: Lead): void {
    if (lead.id == null) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Inc 3/37/38: reabrir usa SOLO POST /reopen. El backend elige el ciclo
    // "Reintentando"; se elimina la búsqueda cliente del ciclo y el mensaje
    // "No hay ciclo disponible".
    this.leadApi.reopenLead(lead.id, businessId).subscribe({
      next: () => {
        this.cargarCatalogos();
        this.cargarLeads();
        this.toast.success({ title: 'Lead reabierto', message: 'El lead ha sido reactivado' });
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo reabrir el lead' });
      }
    });
  }

  // ──────────────────────────────────────────────
  // Selección múltiple
  // ──────────────────────────────────────────────

  toggleLeadSelection(id?: number): void {
    if (id === undefined) return;
    const idx = this.selectedLeadIds.indexOf(id);
    if (idx === -1) {
      this.selectedLeadIds.push(id);
    } else {
      this.selectedLeadIds.splice(idx, 1);
    }
  }

  isLeadSelected(id?: number): boolean {
    return id !== undefined && this.selectedLeadIds.includes(id);
  }

  toggleAllLeads(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedLeadIds = checked
      ? this.leads.map(l => l.id).filter((id): id is number => id !== undefined)
      : [];
  }

  get isAllLeadsSelected(): boolean {
    return this.leads.length > 0 && this.selectedLeadIds.length === this.leads.length;
  }

  // ──────────────────────────────────────────────
  // Acciones masivas
  // ──────────────────────────────────────────────

  openBulkActionModal(action: LeadBulkAction): void {
    this.bulkAction = action;
    this.bulkResult = null;
    this.bulkOwnerId = null;
    this.bulkCycleId = null;
    this.isBulkActionModalOpen = true;
  }

  closeBulkActionModal(): void {
    this.isBulkActionModalOpen = false;
    this.bulkResult = null;
    this.bulkOwnerId = null;
    this.bulkCycleId = null;
  }

  onBulkOwnerChange(userId: number | null): void {
    this.bulkOwnerId = userId;
  }

  getBulkActionLabel(action: LeadBulkAction): string {
    switch (action) {
      case 'CHANGE_OWNER': return 'Cambiar responsable';
      case 'CHANGE_CYCLE': return 'Cambiar etapa';
      case 'DELETE':       return 'Eliminar';
      default:             return action;
    }
  }

  /** Nombre que devuelve el backend en `failures[].name`; nunca el id. */
  getFailureName(f: LeadBulkActionFailure): string {
    return f.name?.trim() || 'Lead sin nombre disponible';
  }

  ejecutarAccionMasiva(): void {
    if (this.selectedLeadIds.length === 0) return;
    if (this.bulkAction === 'CHANGE_OWNER' && !this.bulkOwnerId) return;
    if (this.bulkAction === 'CHANGE_CYCLE' && !this.bulkCycleId) return;

    let params: LeadBulkActionParams | undefined;
    if (this.bulkAction === 'CHANGE_OWNER') {
      params = { userPropietyId: this.bulkOwnerId! };
    } else if (this.bulkAction === 'CHANGE_CYCLE') {
      params = { cycleId: this.bulkCycleId! };
    }

    this.isExecutingBulk = true;
    this.leadApi.bulkActionLeads({
      action: this.bulkAction,
      leadIds: this.selectedLeadIds,
      params
    }).subscribe({
      next: (res) => {
        this.bulkResult = res;
        this.isExecutingBulk = false;
        this.selectedLeadIds = [];
        this.cargarLeads();

        if (res.failed === 0) {
          this.toast.success({
            title: this.getBulkActionLabel(this.bulkAction),
            message: `${res.processed} lead(s) procesado(s) correctamente.`
          });
        } else {
          this.toast.info({
            title: 'Acción completada parcialmente',
            message: `${res.processed} procesado(s), ${res.failed} con error.`
          });
        }
      },
      error: (err) => {
        this.isExecutingBulk = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo ejecutar la acción masiva.'
        });
      }
    });
  }

  // ── Picker de responsable (CHANGE_OWNER) — client-side: el endpoint de usuarios no filtra por texto ──
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.leadApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.toLowerCase();
        const items = t
          ? users.filter(u => `${u.fullname} ${u.lastname ?? ''} ${u.mail}`.toLowerCase().includes(t))
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 }))
    );
  };

  userLabel = (u: CrmUserResponse): string => `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  // Auxiliary methods
  getLeadStateName(id?: number): string {
    if (!id) return '—';
    const state = this.leadStates.find(s => s.id === id);
    return state?.name || '—';
  }

  getLeadCycleName(id?: number): string {
    if (!id) return '—';
    const cycle = this.leadCycles.find(c => c.id === id);
    return cycle?.name || '—';
  }

  // Inc 12: la columna "Condición" refleja el campo `condicion` del backend
  // ("Activo"/"Descalificado"); un lead descalificado nunca aparece "Activo".
  getCondicionColor(lead: Lead): BadgeColor {
    return this.isLeadDisqualified(lead) ? 'error' : 'success';
  }

  getCondicionLabel(lead: Lead): string {
    if (lead.condicion) return lead.condicion;
    return this.isLeadDisqualified(lead) ? 'Descalificado' : 'Activo';
  }

  isLeadDisqualified(lead: Lead): boolean {
    if (lead.condicion) return lead.condicion.toLowerCase() === 'descalificado';
    if (lead.isActive !== undefined) return !lead.isActive;
    return !!lead.isDisqualified;
  }

  // Inc 33: última actividad formateada.
  formatLastActivity(iso?: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarLeads();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean { return this.globalPage > 0; }
  get hasNextPage(): boolean { return this.globalPage < this.globalTotalPages - 1; }
  get pageDisplay(): number  { return this.globalPage + 1; }
}
